import React from 'react';

import { Box, Button, Icon, Input, Label, Select } from '@betterment/samba-react';
import { token } from '@betterment/samba-styled-system/tokens';
import { MultiSelect } from '@betterment/style-closet-react';

import { ActivityAdvisorType, DateRangeOption } from '../../../graphql/schema';
import { safeDateString } from '../../../utils/date';
import { pluralize } from '../../../utils/strings/strings';
import {
  ActivityItemFilter,
  ClientItem,
  FilterKey,
  FilterOptionsMap,
  LegalAccountTypeItem,
  TransactionTypeItem,
} from '../hooks/useFilterOptions';

type FilterRowProps = {
  options: FilterOptionsMap;
  current: ActivityItemFilter;
  onChange: (filters: ActivityItemFilter) => void;
  isClientPageFilter?: boolean;
  downloadCSV?: () => Promise<void>;
};

type Values = { value: string };
type FilterValue = ActivityAdvisorType | DateRangeOption;

const isAllSelected = (selected: string[], allOptions: string[]) =>
  selected.length === allOptions.length && allOptions.every(val => selected.includes(val));

const getDisplayValues = (selected: string[], allOptions: string[]) =>
  isAllSelected(selected, allOptions) ? ['all', ...selected] : selected;

const selectInputFlex = '1 0 192px';

const multiSelectStyles = {
  flex: selectInputFlex,
  // Height of the Select Input
  '& button': {
    height: 'size.600',
    borderRadius: 'medium',
  },
  // Size of the dropdown caret icon
  '& button svg': {
    width: '12px',
  },
  // Size of the 'clear all' button in the multiselect dropdown
  '& ul li div button': {
    height: 'size.300',
  },
};

export const FilterRow: React.FC<FilterRowProps> = ({
  options,
  current,
  onChange,
  isClientPageFilter,
  downloadCSV,
}) => {
  const setFilter = (
    key: Extract<FilterKey, 'advisorType' | 'timeframe'>,
    value: { disabled?: boolean; text?: string; value: FilterValue }
  ) => {
    onChange({ ...current, [key]: value });
  };

  const setMultiFilter = <K extends Extract<FilterKey, 'transactionType' | 'legalAccountType' | 'clients'>>(
    key: K,
    value: ActivityItemFilter[K]
  ) => {
    const incomingValuesAsStrings = value.map((i: Values) => i.value);

    const allPossibleValues = options[key].map((opt: Values) => opt.value);
    const currentValues = current[key].map((opt: Values) => opt.value);
    const noOptions: ActivityItemFilter[K] = [];

    const isAllSelectedInState = isAllSelected(currentValues, allPossibleValues);
    const hasUserClickedAll = incomingValuesAsStrings.includes('all');
    const hasUserJustSelectedAll = hasUserClickedAll && !isAllSelectedInState;
    const hasUserJustDeselectedAll = isAllSelectedInState && !hasUserClickedAll;

    if (hasUserJustSelectedAll) {
      const allOptions = options[key];
      onChange({ ...current, [key]: allOptions });
      return;
    }

    if (hasUserJustDeselectedAll) {
      onChange({ ...current, [key]: noOptions });
      return;
    }

    if (incomingValuesAsStrings.length === 0) {
      onChange({ ...current, [key]: noOptions });
      return;
    }

    const filtered = value.filter(item => item.value !== 'all');
    onChange({ ...current, [key]: filtered });
  };

  const buildItems = (key: Extract<FilterKey, 'transactionType' | 'legalAccountType' | 'clients'>) => {
    const text = {
      legalAccountType: 'All accounts',
      transactionType: 'All transaction types',
      clients: 'All clients',
    }[key];

    return [{ text, value: 'all' }, ...options[key]];
  };

  const buildValues = (key: Extract<FilterKey, 'transactionType' | 'legalAccountType' | 'clients'>) =>
    getDisplayValues(
      current[key].map((i: Values) => i.value),
      options[key].map((i: Values) => i.value)
    );

  const buildSelectedDisplayValue = (filterType: string, filterItems: { value: string | number }[]) => {
    const filtered = filterItems.filter(filter => filter.value !== 'all');

    return pluralize(filtered.length, filterType);
  };

  // @ts-expect-error TS18048 violates strict null checks
  const isCustomTimeframe = current.timeframe.value === DateRangeOption.Custom;

  return (
    <Box
      css={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 'space.200',
        flexWrap: 'wrap',
      }}
    >
      <Box css={{ flex: selectInputFlex }} hidden={isClientPageFilter}>
        <Select
          value={current.advisorType.value ?? undefined}
          onValueChange={nextValue => {
            const item = options.advisorType.find(o => String(o.value) === nextValue);
            if (!item) return;

            setFilter('advisorType', item);
          }}
        >
          <Select.Trigger
            appearance="default"
            aria-label="ClientType"
            id="advisortype-dropdown"
            css={{ width: '100%' }}
          >
            <Select.Value placeholder="Select client type..." />
          </Select.Trigger>

          <Select.Content>
            {options.advisorType.map(item => (
              <Select.Item key={item.value} value={item.value}>
                {`${item.text} clients`}
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
      </Box>

      <Box css={multiSelectStyles} hidden={isClientPageFilter}>
        <MultiSelect
          aria-label="ClientName"
          fullWidth={true}
          getSelectDisplayValue={clients => {
            return buildSelectedDisplayValue('client', clients);
          }}
          name="clientNames"
          items={buildItems('clients')}
          values={buildValues('clients')}
          id="clientNames-multiselect"
          placeholder="No clients selected"
          visibleItems={6}
          style={{
            borderColor: token.var('colors.border.input'),
            height: 'size.600',
            borderRadius: 'medium',
          }}
          onChange={items => setMultiFilter('clients', items as ClientItem[])}
        />
      </Box>

      <Box css={multiSelectStyles}>
        <MultiSelect
          aria-label="legalAccountType"
          fullWidth={true}
          getSelectDisplayValue={legalAccountTypes => {
            return buildSelectedDisplayValue('account', legalAccountTypes);
          }}
          name="accounts"
          items={buildItems('legalAccountType')}
          values={buildValues('legalAccountType')}
          id="accounts-multiselect"
          placeholder="No accounts selected"
          visibleItems={6}
          style={{ borderColor: token.var('colors.border.input') }}
          onChange={items => setMultiFilter('legalAccountType', items as LegalAccountTypeItem[])}
        />
      </Box>

      <Box css={multiSelectStyles}>
        <MultiSelect
          aria-label="TransactionType"
          fullWidth={true}
          name="transactionType"
          getSelectDisplayValue={transactionTypes => {
            return buildSelectedDisplayValue('transaction type', transactionTypes);
          }}
          items={buildItems('transactionType')}
          values={buildValues('transactionType')}
          id="transactionType-multiselect"
          placeholder="No transaction types selected"
          visibleItems={6}
          style={{ borderColor: token.var('colors.border.input') }}
          onChange={items => setMultiFilter('transactionType', items as TransactionTypeItem[])}
        />
      </Box>

      <Box css={{ flex: selectInputFlex }}>
        <Select
          value={current.timeframe?.value ?? undefined}
          onValueChange={nextValue => {
            const item = options.timeframe.find(o => String(o.value) === nextValue);

            if (!item) return;

            if (item.value === DateRangeOption.Custom) {
              onChange({
                ...current,
                startTimeInclusive: null,
                endTimeExclusive: null,
              });
            }

            setFilter('timeframe', item);
          }}
        >
          <Select.Trigger appearance="default" aria-label="Timeframe" id="timeframe-dropdown" css={{ width: '100%' }}>
            <Select.Value placeholder="Select timeframe..." />
          </Select.Trigger>

          <Select.Content>
            {options.timeframe.map(item => (
              <Select.Item key={item.value} value={item.value}>
                {item.text}
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
        {isCustomTimeframe && (
          <Box css={{ display: 'flex', gap: 'space.100', py: 'space.100', flexDirection: 'column' }}>
            <Box css={{ display: 'flex', gap: 'space.100' }}>
              <Box css={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Label htmlFor="startTimeInclusive">From</Label>
                <Input
                  id="startTimeInclusive"
                  name="startTimeInclusive"
                  type="date"
                  // @ts-expect-error TS2322 violates strict null checks
                  value={safeDateString(current.startTimeInclusive)}
                  onChange={e => onChange({ ...current, startTimeInclusive: new Date(e.target.value) })}
                  // @ts-expect-error TS2322 violates strict null checks
                  max={current.endTimeExclusive ? safeDateString(current.endTimeExclusive) : safeDateString(new Date())}
                />
              </Box>
              <Box css={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Label htmlFor="endTimeExclusive">To</Label>
                <Input
                  id="endTimeExclusive"
                  name="endTimeExclusive"
                  type="date"
                  // @ts-expect-error TS2322 violates strict null checks
                  value={safeDateString(current.endTimeExclusive)}
                  onChange={e => onChange({ ...current, endTimeExclusive: new Date(e.target.value) })}
                  // @ts-expect-error TS2322 violates strict null checks
                  min={safeDateString(current.startTimeInclusive)}
                  // @ts-expect-error TS2322 violates strict null checks
                  max={safeDateString(new Date())}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <Box
        css={{
          display: 'flex',
          width: 'auto',
          alignItems: 'center',
        }}
      >
        <Box
          css={{
            display: 'flex',
            width: 'auto',
            minWidth: 0,
          }}
        >
          <Button
            onClick={() => {
              // @ts-expect-error TS2722 violates strict null checks
              void downloadCSV();
            }}
            variant="textPrimary"
          >
            <Icon
              name="Download"
              size="small"
              css={{ verticalAlign: 'middle', display: 'inline-block', marginBottom: 16, width: 'size.400' }}
            />
            CSV
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
